/**
 * One place that forgets the signed-in user's in-memory data when the user changes.
 *
 * Stores and module caches that hold a user's data register a reset here when
 * their module loads; `resetUserScopedState()` runs every registered reset when
 * the signed-in identity changes (sign-out, session end, account switch). Before
 * this, each store needed its own hook into the auth store, and a store that
 * never got one (Household) kept the last user's data — with a 30s freshness
 * window, the next user's first show skipped its fetch and rendered it.
 *
 * Deliberately dependency-free (no app-store import), so registering costs a
 * store nothing in suites that mock the app store.
 */
import { logger } from '@/utils/logger';

const resets = new Map<string, () => void>();

/**
 * Register `reset` under a stable `name`. Re-registering a name replaces the
 * earlier reset, so a hot-reloaded module never leaves a stale one behind.
 */
export function registerUserScopedReset(name: string, reset: () => void): void {
  resets.set(name, reset);
}

/** Run every registered reset; one that throws never stops the rest. */
export function resetUserScopedState(): void {
  resets.forEach((reset, name) => {
    try {
      reset();
    } catch (error) {
      logger.error(`User-scoped reset "${name}" failed:`, error);
    }
  });
}

/** Names of the registered resets, for the coverage test. */
export function registeredUserScopedResets(): string[] {
  return [...resets.keys()];
}
